import { env } from "@repo/env";

import { getEnvironment } from "../environment";

// Environment-aware CloudFront domain for public distribution
export function getDistributionDomain(
  envOverride?: "dev" | "staging" | "prod",
): string {
  const deployEnv = envOverride ?? getEnvironment();

  let domain: string | undefined;

  // Get environment-specific CloudFront domain
  switch (deployEnv) {
    case "dev":
      domain =
        env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_DEV ??
        env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN;
      break;
    case "staging":
      domain =
        env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_STAGING ??
        env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN;
      break;
    case "prod":
    default:
      domain = env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN;
      break;
  }

  if (!domain) {
    throw new Error(
      `CloudFront domain is required but not set for environment: ${deployEnv}. Please add appropriate variables to your Infisical environment.`,
    );
  }

  return domain;
}

// Convert S3 key to public CloudFront URL
export function getCloudFrontUrl(
  s3Key: string,
  envOverride?: "dev" | "staging" | "prod",
): string {
  const domain = getDistributionDomain(envOverride);
  const cleanKey = s3Key.replace(/^\/+/, "");
  return `https://${domain}/${cleanKey}`;
}

/**
 * Transforms a storage reference (S3 key or URL) to a public-facing URL.
 *
 * This is the primary function to use when returning image URLs in API responses.
 * Handles all edge cases:
 * - Null/undefined values → returns null
 * - Already-full URLs (http/https) → returns as-is (external OAuth profile pics)
 * - S3 keys → transforms to CloudFront URL
 *
 * @example
 * // Returns null
 * toPublicUrl(null)
 * toPublicUrl(undefined)
 *
 * // Returns the URL as-is (external OAuth)
 * toPublicUrl("https://lh3.googleusercontent.com/...")
 * toPublicUrl("https://media.licdn.com/...")
 *
 * // Transforms S3 key to CloudFront URL
 * toPublicUrl("users/abc123/avatar/image.png")
 * // → "https://cdn.terac.com/users/abc123/avatar/image.png"
 */
export function toPublicUrl(
  storageRef: string | null | undefined,
  envOverride?: "dev" | "staging" | "prod",
): string | null {
  if (!storageRef) {
    return null;
  }

  // Already a full URL (external OAuth profile pics, etc.)
  if (storageRef.startsWith("http://") || storageRef.startsWith("https://")) {
    return storageRef;
  }

  // S3 key - transform to CloudFront URL using shared logic
  return getCloudFrontUrl(storageRef, envOverride);
}
